import { VoteStatus } from "types/voteEnum";

/**
 * A voter is locked out as soon as the session forbids updating an answer
 * (`canAnswerBeUpdated`) and an answer has already been recorded for them —
 * either during this session, or on a previous one (server side).
 */
export function isAnswerLocked(
    voteStatus: VoteStatus,
    hasAlreadyAnswered: boolean,
    canAnswerBeUpdated: boolean,
): boolean {
    return (
        !canAnswerBeUpdated &&
        (VoteStatus.Success === voteStatus || hasAlreadyAnswered)
    );
}

export function hasPersonAlreadyAnswered(
    personId: string,
    answeredVoterIds?: Array<string>,
): boolean {
    return answeredVoterIds?.includes(personId) ?? false;
}

/**
 * Status a voter starts on when the question is (re)opened: a vote already
 * recorded server side is restored as a success, so the UI shows it as sent
 * and every proposal is disabled.
 */
export function createInitialAnswerStatus(
    hasAlreadyAnswered: boolean,
    idleStatus: VoteStatus = VoteStatus.Idle,
): VoteStatus {
    return hasAlreadyAnswered ? VoteStatus.Success : idleStatus;
}

/**
 * Rolls back every change the reducer computed for voters whose answer is
 * final. Applied once around a reducer, it removes the need to guard each
 * individual action.
 */
export function rollbackLockedVotes<Vote extends { voterId: string }>(
    previousVotes: Array<Vote>,
    nextVotes: Array<Vote>,
    isLocked: (vote: Vote) => boolean,
): Array<Vote> {
    return nextVotes.map((vote) => {
        const previousVote = previousVotes.find(
            (candidate) => candidate.voterId === vote.voterId,
        );

        if (previousVote && isLocked(previousVote)) {
            return previousVote;
        }

        return vote;
    });
}
